// CTSK-256 — the registry cleaning modal (KDP §3.7 basic cleaning
// affordance). Opened from the per-tab Clean button on the registry
// tab surfaces (DSR + Timeline Lane), which is itself gated behind the
// CTSK-268 slice B EditLockToggle — reaching this modal is already a
// deliberate operator signal.
//
// Flow (deliberately two-step; the server defaults to dryRun true):
//   1. Operator checks one or more of the 3 primitives (all unchecked
//      on open) → "Preview" runs the dry-run call and renders the diff
//      (before/after counts + up to N example rows).
//   2. "Apply" stays disabled until a preview has succeeded for the
//      CURRENT selection AND the operator ticks the explicit confirm
//      checkbox — then the full call goes out with dryRun: false.
//      Changing the operation selection invalidates the preview (and
//      the confirmation) so what you apply is always what you saw.
//
// Success toast: "Cleaned X entries: Y deduped, Z normalized, W
// migrated" — counts come from the server's operationCounts, which
// carry true totals even when the diff examples are capped.

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiSingle } from '../lib/api.js';
import { toast } from './Toast.js';

// Keep in lockstep with REGISTRY_CLEAN_OPERATIONS in
// packages/shared/src/schemas/tool.ts (canonical order).
const OPERATIONS = [
  {
    key: 'dedupe' as const,
    label: 'Dedupe on id',
    hint: 'Remove entries whose id duplicates an earlier entry (first occurrence wins)',
  },
  {
    key: 'normalize' as const,
    label: 'Normalize whitespace',
    hint: 'Trim + collapse internal whitespace on string fields',
  },
  {
    key: 'migrate' as const,
    label: 'Migrate schema version',
    hint: 'Apply declared schema migration rules (no schema declares any today — no-op)',
  },
];
type OperationKey = (typeof OPERATIONS)[number]['key'];

interface CleanDiffRemoved { index: number; id: string }
interface CleanDiffChanged { index: number; id: string | null; before: unknown; after: unknown }

export interface CleanResult {
  documentId: string;
  path: string;
  dryRun: boolean;
  entryCount: { before: number; after: number };
  operationCounts: { dedupe: number; normalize: number; migrate: number };
  diff: { added: unknown[]; removed: CleanDiffRemoved[]; changed: CleanDiffChanged[] };
  diffTruncated: boolean;
  preview: string;
  truncated: boolean;
}

/** Example rows rendered per diff bucket. The server already caps the
 *  wire payload at 50; this is the smaller display cap. */
const EXAMPLE_ROW_CAP = 10;

export interface CleanRegistryModalProps {
  toolId: string;
  /** Registry file path, e.g. 'bookmarks.yaml'. */
  path: string;
  onClose: () => void;
}

export function CleanRegistryModal({ toolId, path, onClose }: CleanRegistryModalProps) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<OperationKey>>(new Set());
  const [preview, setPreview] = useState<CleanResult | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const operations = useMemo(
    () => OPERATIONS.map((o) => o.key).filter((k) => selected.has(k)),
    [selected],
  );

  const toggleOp = (key: OperationKey) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
    // The preview (and the confirmation that rides on it) describes a
    // specific selection — changing the selection invalidates both.
    setPreview(null);
    setConfirmed(false);
  };

  const previewMut = useMutation({
    mutationFn: () =>
      apiSingle<CleanResult>(`/tools/${toolId}/registry/clean`, {
        method: 'POST',
        body: { path, operations, dryRun: true },
      }),
    onSuccess: (result) => {
      setPreview(result);
      setConfirmed(false);
    },
    onError: (err: unknown) => {
      setPreview(null);
      toast.fromError(err, `Failed to preview cleaning ${path}`);
    },
  });

  const applyMut = useMutation({
    mutationFn: () =>
      apiSingle<CleanResult>(`/tools/${toolId}/registry/clean`, {
        method: 'POST',
        body: { path, operations, dryRun: false },
      }),
    onSuccess: (result) => {
      const { dedupe, normalize, migrate } = result.operationCounts;
      const total = dedupe + normalize + migrate;
      toast.success(`Cleaned ${total} entries: ${dedupe} deduped, ${normalize} normalized, ${migrate} migrated`);
      // The tab body re-renders from the content query; the activity
      // panel (CTSK-268 slice C) refreshes from the audit trail.
      qc.invalidateQueries({ queryKey: ['tool-registry-content'] });
      qc.invalidateQueries({ queryKey: ['document-activity', result.documentId] });
      onClose();
    },
    onError: (err: unknown) => {
      toast.fromError(err, `Failed to clean ${path}`);
    },
  });

  const busy = previewMut.isPending || applyMut.isPending;

  // ESC closes (unless a call is in flight).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, busy]);

  const changeTotal = preview
    ? preview.operationCounts.dedupe + preview.operationCounts.normalize + preview.operationCounts.migrate
    : 0;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Clean ${path}`}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          background: 'var(--surface-raised)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-strong)',
          width: 'min(720px, 100%)',
          maxHeight: '90vh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          padding: 16,
          gap: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
            Clean{' '}
            <code style={{ fontFamily: 'var(--font-mono)', background: 'var(--surface-sunken)', padding: '1px 5px', borderRadius: 4 }}>
              {path}
            </code>
          </h2>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            Preview first — nothing is written until you apply.
          </span>
        </div>

        {/* Operation checkboxes — all unchecked by default */}
        <fieldset style={{ border: '1px solid var(--border-subtle)', borderRadius: 6, padding: 12, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <legend style={{ fontSize: 12, color: 'var(--text-muted)', padding: '0 4px' }}>Operations</legend>
          {OPERATIONS.map((op) => (
            <label key={op.key} style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 13, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={selected.has(op.key)}
                onChange={() => toggleOp(op.key)}
                disabled={busy}
              />
              <span>
                {op.label}
                <span style={{ display: 'block', fontSize: 11, color: 'var(--text-muted)' }}>{op.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>

        {/* Preview result — diff summary + example rows */}
        {preview && (
          <div data-testid="clean-preview" style={{ background: 'var(--surface-sunken)', borderRadius: 6, padding: 12, fontSize: 13, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div>
              <strong>
                {preview.entryCount.before} → {preview.entryCount.after} entries
              </strong>{' '}
              <span style={{ color: 'var(--text-muted)' }}>
                · {preview.operationCounts.dedupe} deduped · {preview.operationCounts.normalize} normalized · {preview.operationCounts.migrate} migrated
              </span>
            </div>
            {changeTotal === 0 && (
              <div style={{ color: 'var(--text-muted)' }}>Nothing to clean — the file is already tidy for the selected operations.</div>
            )}
            {preview.diff.removed.length > 0 && (
              <div>
                <div style={{ fontWeight: 600, fontSize: 12 }}>Removed (duplicate ids)</div>
                <ul style={{ margin: '4px 0 0 0', paddingLeft: 18 }}>
                  {preview.diff.removed.slice(0, EXAMPLE_ROW_CAP).map((r) => (
                    <li key={`${r.index}-${r.id}`} style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                      #{r.index} {r.id}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {preview.diff.changed.length > 0 && (
              <div>
                <div style={{ fontWeight: 600, fontSize: 12 }}>Changed (whitespace normalized)</div>
                <ul style={{ margin: '4px 0 0 0', paddingLeft: 18 }}>
                  {preview.diff.changed.slice(0, EXAMPLE_ROW_CAP).map((c) => (
                    <li key={`${c.index}-${c.id ?? 'noid'}`} style={{ fontSize: 12 }}>
                      <code style={{ fontFamily: 'var(--font-mono)' }}>{c.id ?? `#${c.index}`}</code>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(preview.diffTruncated
              || preview.diff.removed.length > EXAMPLE_ROW_CAP
              || preview.diff.changed.length > EXAMPLE_ROW_CAP) && (
              <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                Example rows are truncated — the counts above are the full totals.
              </div>
            )}
            {changeTotal > 0 && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 12, marginTop: 4 }}>
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  disabled={busy}
                />
                I reviewed the preview — apply these changes to <code style={{ fontFamily: 'var(--font-mono)' }}>{path}</code>
              </label>
            )}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            style={{ padding: '6px 14px', background: 'var(--surface-raised)', color: 'var(--text-secondary)', border: '1px solid var(--border-strong)', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => previewMut.mutate()}
            disabled={busy || operations.length === 0}
            style={{ padding: '6px 14px', background: 'var(--surface-raised)', color: 'var(--text-primary)', border: '1px solid var(--border-strong)', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: busy || operations.length === 0 ? 0.6 : 1 }}
          >
            {previewMut.isPending ? 'Previewing…' : 'Preview'}
          </button>
          <button
            type="button"
            onClick={() => applyMut.mutate()}
            disabled={busy || !preview || !confirmed || changeTotal === 0}
            style={{ padding: '6px 14px', background: 'var(--accent-primary)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: busy || !preview || !confirmed || changeTotal === 0 ? 0.6 : 1 }}
          >
            {applyMut.isPending ? 'Applying…' : 'Apply'}
          </button>
        </div>
      </div>
    </div>
  );
}
